/**
 * Volume detail page component.
 *
 * @module pages/VolumeDetailPage
 */

import { useState, useRef, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import {
  ArrowLeft,
  Trash2,
  Copy,
  Check,
  RefreshCw,
  MoreHorizontal,
  Info,
  HardDrive,
  Box,
  Loader2,
  ChevronRight,
  Lock,
  Cloud,
  GitFork,
} from 'lucide-react';
import { PageHeader } from '@/components/layout';
import {
  Card,
  CardBody,
  Button,
  LoadingPage,
  ConfirmModal,
  SlidePanel,
  Badge,
} from '@/components/ui';
import { VolumeFileExplorer } from '@/components/VolumeFileExplorer';
import { ForkVolumeModal } from '@/components/ForkVolumeModal';
import {
  useVolume,
  useDeleteVolume,
  useForkVolume,
  useSandboxes,
  useSandboxGroupVolume,
  useDeleteSandboxGroupVolume,
  useForkSandboxGroupVolume,
} from '@/hooks';
import type { DataDiskVolumeUsage, VolumeResponse } from '@/types';
import { getSandboxStateBadgeVariant } from '@/components/ui';

/**
 * Format bytes to a human-readable string.
 */
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const sizes = ['B', 'KiB', 'MiB', 'GiB', 'TiB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0)} ${sizes[i]}`;
}

/**
 * Mounted sandboxes section — expandable dropdown showing sandboxes that have this volume mounted.
 */
function MountedSandboxes({ volumeName }: { volumeName: string }) {
  const { data: sandboxes, isLoading } = useSandboxes(1, 100);
  const [isExpanded, setIsExpanded] = useState(false);

  const mounted = useMemo(() => {
    if (!sandboxes) return [];
    return sandboxes
      .filter((s) => {
        if (s.volumes?.some((v) => v.volumeName === volumeName)) return true;
        if (
          s.sourcesRef?.pod?.containers?.some((c) =>
            c.volumeMounts?.some((v) => v.name === volumeName)
          )
        )
          return true;
        return false;
      })
      .flatMap((s) => {
        const entries: {
          id: string;
          state: string;
          mountpoint: string;
          readOnly?: boolean;
          containerName?: string;
        }[] = [];

        // Top-level volume mount
        const topLevel = s.volumes?.find((v) => v.volumeName === volumeName);
        if (topLevel) {
          entries.push({
            id: s.id,
            state: s.state || 'Running',
            mountpoint: topLevel.mountpoint,
            readOnly: topLevel.readOnly,
          });
        }

        // Per-container pod volume mounts (one entry per container)
        const podMounts =
          s.sourcesRef?.pod?.containers?.flatMap((c) =>
            (c.volumeMounts ?? [])
              .filter((v) => v.name === volumeName)
              .map((v) => ({
                id: s.id,
                state: s.state || 'Running',
                mountpoint: v.mountPath,
                readOnly: v.readOnly,
                containerName: c.name,
              }))
          ) ?? [];
        entries.push(...podMounts);

        return entries;
      });
  }, [sandboxes, volumeName]);

  return (
    <div className="mt-4 w-fit rounded-lg border-2 border-gray-200 dark:border-[#2a2a2a] bg-white dark:bg-[#0a0a0a]">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-2 bg-gray-50 dark:bg-[#161616] border-b border-gray-200 dark:border-[#2a2a2a] flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-[#1a1a1a] transition-colors rounded-t-lg"
      >
        <ChevronRight
          className={`h-4 w-4 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
        />
        <Box className="h-4 w-4 text-gray-500 dark:text-gray-400" />
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {isLoading
            ? 'Loading sandboxes…'
            : mounted.length === 0
              ? 'Not mounted by any sandbox'
              : `Mounted ${mounted.length} ${mounted.length === 1 ? 'time' : 'times'} across sandboxes`}
        </span>
        {isLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />}
      </button>
      <div
        className={`grid transition-[grid-template-rows] duration-200 ease-out ${isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
      >
        <div className="overflow-hidden">
          {mounted.length > 0 ? (
            <div className="divide-y divide-gray-100 dark:divide-[#2a2a2a]">
              {mounted.map((s, idx) => (
                <div
                  key={`${s.id}-${s.containerName ?? 'top'}-${idx}`}
                  className="px-4 py-2 flex items-center gap-3 text-sm"
                >
                  <Link
                    to={`/sandboxes/${encodeURIComponent(s.id)}`}
                    className="font-mono text-primary-600 dark:text-primary-400 hover:underline truncate max-w-[260px]"
                    title={s.id}
                  >
                    {s.id}
                  </Link>
                  <Badge variant={getSandboxStateBadgeVariant(s.state)}>{s.state}</Badge>
                  <span className="text-gray-400 dark:text-gray-500" title="Mount path">
                    {s.mountpoint}
                  </span>
                  {s.containerName && (
                    <span className="text-xs font-mono text-gray-400 dark:text-gray-500">
                      ({s.containerName})
                    </span>
                  )}
                  {s.readOnly && (
                    <span className="text-xs text-gray-400 dark:text-gray-500 italic">
                      read-only
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : !isLoading ? (
            <div className="px-4 py-3 text-sm text-gray-400 dark:text-gray-500">
              No sandboxes are currently using this volume.
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * Volume detail page
 */
export function VolumeDetailPage() {
  const { name, groupId } = useParams<{ name: string; groupId?: string }>();
  const navigate = useNavigate();

  // URL prefix for links
  const basePath = groupId ? `/sandbox-groups/${encodeURIComponent(groupId)}/volumes` : '/volumes';

  // Group-scoped data (enabled only when groupId is present)
  const {
    data: groupVolume,
    isLoading: groupLoading,
    error: groupError,
    refetch: groupRefetch,
    isFetching: groupFetching,
  } = useSandboxGroupVolume(groupId ?? '', name!);
  const deleteGroupMutation = useDeleteSandboxGroupVolume();
  const forkGroupMutation = useForkSandboxGroupVolume();

  // Default (non-group) data (disabled when groupId is present)
  const {
    data: userVolume,
    isLoading: userLoading,
    error: userError,
    refetch: userRefetch,
    isFetching: userFetching,
  } = useVolume(name!, !groupId);
  const deleteMutation = useDeleteVolume();
  const forkMutation = useForkVolume();

  const volume = groupId ? groupVolume : userVolume;
  const isLoading = groupId ? groupLoading : userLoading;
  const error = groupId ? groupError : userError;
  const refetch = groupId ? groupRefetch : userRefetch;
  const isFetching = groupId ? groupFetching : userFetching;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showForkModal, setShowForkModal] = useState(false);
  const [forkError, setForkError] = useState<string | null>(null);
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const actionsMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(e.target as Node)) {
        setShowActionsMenu(false);
      }
    }
    if (showActionsMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showActionsMenu]);

  const handleDelete = () => {
    if (volume) {
      if (groupId) {
        deleteGroupMutation.mutate(
          { groupId, volumeName: volume.volumeName },
          { onSuccess: () => navigate(basePath) }
        );
      } else {
        deleteMutation.mutate(volume.volumeName, {
          onSuccess: () => navigate('/volumes'),
        });
      }
    }
  };

  const handleFork = (destinationVolumeName: string) => {
    if (!volume) return;
    setForkError(null);
    const onSuccess = (data: VolumeResponse) => {
      setShowForkModal(false);
      setForkError(null);
      navigate(`${basePath}/${encodeURIComponent(data.volumeName)}`);
    };
    const onError = (error: Error) => {
      setForkError(error.message);
    };

    if (groupId) {
      forkGroupMutation.mutate(
        {
          groupId,
          sourceVolumeName: volume.volumeName,
          destinationVolumeName,
          labels: { 'fork-source': volume.volumeName },
        },
        { onSuccess, onError }
      );
    } else {
      forkMutation.mutate(
        {
          sourceVolumeName: volume.volumeName,
          destinationVolumeName,
          labels: { 'fork-source': volume.volumeName },
        },
        { onSuccess, onError }
      );
    }
  };

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  if (isLoading) {
    return <LoadingPage />;
  }

  if (error || !volume) {
    return (
      <div>
        <PageHeader
          title="Volume Not Found"
          breadcrumbs={[
            { label: 'Dashboard', href: '/dashboard' },
            { label: 'Volumes', href: basePath },
            { label: 'Not Found' },
          ]}
        />
        <Card>
          <CardBody>
            <p className="text-gray-500 dark:text-gray-400">
              The requested volume could not be found.
            </p>
            <Link to={basePath}>
              <Button
                variant="secondary"
                className="mt-4"
                leftIcon={<ArrowLeft className="h-4 w-4" />}
              >
                Back to Volumes
              </Button>
            </Link>
          </CardBody>
        </Card>
      </div>
    );
  }

  const isDataDisk = volume.type === 'DataDisk';
  // For DataDisk volumes, the `usage` field from the backend is a DataDiskVolumeUsageView
  // (polymorphic JSON), not the blob VolumeUsage shape.
  const dataDiskUsage = isDataDisk
    ? (volume.usage as unknown as DataDiskVolumeUsage | undefined)
    : undefined;

  return (
    <div>
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2.5">
            {volume.volumeName}
            {isDataDisk ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-purple-50 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400 text-xs font-medium">
                <HardDrive className="h-3 w-3" />
                Data Disk
              </span>
            ) : volume.type?.toLowerCase().includes('blob') ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 text-xs font-medium">
                <Cloud className="h-3 w-3" />
                Azure Blob
              </span>
            ) : null}
          </span>
        }
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Volumes', href: basePath },
          { label: volume.volumeName },
        ]}
        action={
          <div className="flex flex-wrap gap-2">
            <Link to={basePath}>
              <Button variant="secondary" leftIcon={<ArrowLeft className="h-4 w-4" />}>
                Back
              </Button>
            </Link>
            <div className="relative" ref={actionsMenuRef}>
              <Button
                variant="secondary"
                onClick={() => setShowActionsMenu(!showActionsMenu)}
                leftIcon={<MoreHorizontal className="h-4 w-4" />}
              >
                Actions
              </Button>
              {showActionsMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg shadow-lg z-50">
                  <button
                    onClick={() => {
                      setShowInfoPanel(true);
                      setShowActionsMenu(false);
                    }}
                    className="flex items-center gap-3 w-full px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#2a2a2a] rounded-t-lg"
                  >
                    <Info className="h-4 w-4" />
                    Details
                  </button>
                  {isDataDisk && (
                    <button
                      onClick={() => {
                        setShowForkModal(true);
                        setShowActionsMenu(false);
                      }}
                      className="flex items-center gap-3 w-full px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#2a2a2a]"
                    >
                      <GitFork className="h-4 w-4" />
                      Fork
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setShowDeleteModal(true);
                      setShowActionsMenu(false);
                    }}
                    disabled={volume.isAttached}
                    className="flex items-center gap-3 w-full px-4 py-3 text-sm text-danger-600 dark:text-danger-400 hover:bg-gray-100 dark:hover:bg-[#2a2a2a] rounded-b-lg disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent dark:disabled:hover:bg-transparent"
                    title={
                      volume.isAttached ? 'Cannot delete while attached to a sandbox' : undefined
                    }
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        }
      />

      {/* Usage — Azure Blob volumes */}
      {!isDataDisk && (
        <div className="w-fit rounded-lg border-2 border-gray-200 dark:border-[#2a2a2a] bg-white dark:bg-[#0a0a0a]">
          <div className="px-4 py-2 bg-gray-50 dark:bg-[#161616] border-b border-gray-200 dark:border-[#2a2a2a] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <HardDrive className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Usage</span>
            </div>
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
              aria-label="Refresh usage"
              title="Refresh usage"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
            </button>
          </div>
          <div className="px-4 py-3 flex flex-wrap gap-x-8 gap-y-2 text-sm">
            {volume.usage ? (
              <>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 dark:text-gray-400">Storage</span>
                  <span className="text-gray-900 dark:text-gray-100">
                    {formatBytes(volume.usage.usedBytes)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 dark:text-gray-400">Blobs</span>
                  <span className="text-gray-900 dark:text-gray-100">
                    {volume.usage.itemCount.toLocaleString()}
                  </span>
                  <span className="relative group">
                    <Info className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 cursor-help" />
                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2.5 py-1.5 text-xs text-white bg-gray-900 dark:bg-gray-700 rounded-md whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity">
                      Files and symlinks stored in the volume.
                    </span>
                  </span>
                </div>
              </>
            ) : (
              <span className="text-gray-400 dark:text-gray-500">No usage data available</span>
            )}
          </div>
        </div>
      )}

      {/* Data Disk Info */}
      {isDataDisk && (
        <div className="w-fit rounded-lg border-2 border-gray-200 dark:border-[#2a2a2a] bg-white dark:bg-[#0a0a0a]">
          <div className="px-4 py-2 bg-gray-50 dark:bg-[#161616] border-b border-gray-200 dark:border-[#2a2a2a] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <HardDrive className="h-4 w-4 text-purple-500 dark:text-purple-400" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Data Disk Info
              </span>
            </div>
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
              aria-label="Refresh"
              title="Refresh"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
            </button>
          </div>
          <div className="px-4 py-3 flex flex-wrap gap-x-8 gap-y-2 text-sm">
            {volume.size && (
              <div className="flex items-center gap-2">
                <span className="text-gray-500 dark:text-gray-400">Size</span>
                <span className="text-gray-900 dark:text-gray-100 font-medium">{volume.size}</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <span className="text-gray-500 dark:text-gray-400">State</span>
              {volume.isAttached ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                  <Lock className="h-3 w-3" />
                  Attached
                </span>
              ) : (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                  Available
                </span>
              )}
            </div>
            {volume.clusterId && (
              <div className="flex items-center gap-2">
                <span className="text-gray-500 dark:text-gray-400">Cluster</span>
                <span className="text-gray-900 dark:text-gray-100 font-mono text-xs">
                  {volume.clusterId}
                </span>
              </div>
            )}
            {dataDiskUsage?.compressedBlobSizeBytes !== null &&
              dataDiskUsage?.compressedBlobSizeBytes !== undefined && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 dark:text-gray-400">Compressed</span>
                  <span className="text-gray-900 dark:text-gray-100">
                    {formatBytes(dataDiskUsage.compressedBlobSizeBytes)}
                  </span>
                </div>
              )}
            {dataDiskUsage?.usedSizeBytes !== null &&
              dataDiskUsage?.usedSizeBytes !== undefined && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 dark:text-gray-400">Disk Usage</span>
                  <span className="text-gray-900 dark:text-gray-100">
                    {formatBytes(dataDiskUsage.usedSizeBytes)}
                  </span>
                </div>
              )}
            {dataDiskUsage?.lastUploadedAtUtc && (
              <div className="flex items-center gap-2">
                <span className="text-gray-500 dark:text-gray-400">Last Upload</span>
                <span className="text-gray-900 dark:text-gray-100">
                  {new Date(dataDiskUsage.lastUploadedAtUtc).toLocaleString()}
                </span>
              </div>
            )}
            {!volume.clusterId && !dataDiskUsage && !volume.isAttached && (
              <div className="flex items-center gap-2">
                <span className="text-gray-400 dark:text-gray-500 italic">Never mounted yet</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mounted Sandboxes */}
      <MountedSandboxes volumeName={volume.volumeName} />

      {/* File Explorer — Azure Blob volumes only */}
      {!isDataDisk && (
        <div className="mt-4">
          <VolumeFileExplorer volumeName={volume.volumeName} groupId={groupId} />
        </div>
      )}

      {/* Info Slide Panel */}
      <SlidePanel
        isOpen={showInfoPanel}
        onClose={() => setShowInfoPanel(false)}
        title="Volume Details"
        description="Volume information"
        size="md"
      >
        <div className="space-y-6">
          {/* Basic Information */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
              Basic Information
            </h4>
            <dl className="space-y-4">
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Volume Name
                </dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-gray-200 flex items-center gap-2">
                  <span className="font-mono">{volume.volumeName}</span>
                  <button
                    onClick={() => handleCopy(volume.volumeName, 'name')}
                    className="text-gray-400 hover:text-gray-600 dark:text-gray-200 dark:hover:text-gray-100"
                    aria-label="Copy volume name"
                  >
                    {copiedField === 'name' ? (
                      <Check className="h-4 w-4 text-success-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Type</dt>
                <dd className="mt-1">
                  {isDataDisk ? (
                    <Badge variant="neutral">Data Disk</Badge>
                  ) : (
                    <Badge variant="info">{volume.type}</Badge>
                  )}
                </dd>
              </div>
              {isDataDisk && volume.size && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Size</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-gray-200">{volume.size}</dd>
                </div>
              )}
              {isDataDisk && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">State</dt>
                  <dd className="mt-1">
                    {volume.isAttached ? (
                      <Badge variant="warning">Attached</Badge>
                    ) : (
                      <Badge variant="success">Available</Badge>
                    )}
                  </dd>
                </div>
              )}
              {isDataDisk && volume.clusterId && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Cluster</dt>
                  <dd className="mt-1 text-sm font-mono text-gray-900 dark:text-gray-200 flex items-center gap-2">
                    {volume.clusterId}
                    <button
                      onClick={() => handleCopy(volume.clusterId!, 'cluster')}
                      className="text-gray-400 hover:text-gray-600 dark:text-gray-200 dark:hover:text-gray-100"
                      aria-label="Copy cluster ID"
                    >
                      {copiedField === 'cluster' ? (
                        <Check className="h-4 w-4 text-success-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </button>
                  </dd>
                </div>
              )}
              {isDataDisk &&
                dataDiskUsage?.compressedBlobSizeBytes !== null &&
                dataDiskUsage?.compressedBlobSizeBytes !== undefined && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Compressed Size
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-gray-200">
                      {formatBytes(dataDiskUsage.compressedBlobSizeBytes)}
                    </dd>
                  </div>
                )}
              {isDataDisk &&
                dataDiskUsage?.usedSizeBytes !== null &&
                dataDiskUsage?.usedSizeBytes !== undefined && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Disk Usage
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-gray-200">
                      {formatBytes(dataDiskUsage.usedSizeBytes)}
                    </dd>
                  </div>
                )}
              {isDataDisk && dataDiskUsage?.lastUploadedAtUtc && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Last Uploaded
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-gray-200">
                    {new Date(dataDiskUsage.lastUploadedAtUtc).toLocaleString()}
                  </dd>
                </div>
              )}
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Labels</dt>
                <dd className="mt-1">
                  {volume.labels && Object.keys(volume.labels).length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(volume.labels).map(([key, value]) => (
                        <span
                          key={key}
                          className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-primary-100 dark:bg-primary-900/20 text-primary-800 dark:text-primary-200"
                        >
                          {key}: {String(value)}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-500 dark:text-gray-400">-</span>
                  )}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </SlidePanel>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Volume"
        message={
          volume.isAttached
            ? `Volume "${volume.volumeName}" is currently attached to a sandbox and cannot be deleted. Detach it first by stopping the sandbox.`
            : `Are you sure you want to delete volume "${volume.volumeName}"? This action cannot be undone.`
        }
        confirmText="Delete"
        variant="danger"
        isLoading={groupId ? deleteGroupMutation.isPending : deleteMutation.isPending}
      />

      {isDataDisk && (
        <ForkVolumeModal
          isOpen={showForkModal}
          onClose={() => {
            setShowForkModal(false);
            setForkError(null);
          }}
          onFork={handleFork}
          sourceVolumeName={volume.volumeName}
          isLoading={groupId ? forkGroupMutation.isPending : forkMutation.isPending}
          error={forkError}
        />
      )}
    </div>
  );
}
